import React from "react";
import {
    Box,
    Heading,
    Text,
    chakra,
    AspectRatio,
    Stack,
} from "@chakra-ui/react";
import SectionCard from "../components/SectionCard";
import Highlight from "../components/Highlight";

const SensorsPage = () => {
    return (
        <Box bg="gray.50" py={8}>
            {/* Header Section */}
            <SectionCard flexDirection="row" delay={0}>
                <Box flex="2" textAlign={{base: "center", md: "left"}}>
                    <chakra.img
                        src="./CAAT_Sensors_Logo.png"
                        alt="Sensor Logo"
                        maxW="300px"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Why Use Sensors in Dog Training?
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Modern dog training increasingly embraces data-driven approaches — and for good reason.
                            While experience and intuition remain essential, sensors allow us to supplement human
                            observation with <Highlight>objective, quantifiable data</Highlight>.
                        </Text>
                        <Text fontSize="md">
                            With sensors, we can measure performance and track progress over time — independent of
                            subjective impressions. This improves the <Highlight>reliability of
                            assessments</Highlight> and enables us to <Highlight>compare training
                            methods</Highlight> and outcomes with precision.
                        </Text>
                        <Text fontSize="md">
                            Real-time feedback empowers trainers to adapt in the moment, while recorded data enables
                            review and adjustment. Over time, this leads to <Highlight>better decisions</Highlight> and
                            more personalized plans.
                        </Text>
                        <Text fontSize="md">
                            Ultimately, sensors help bridge the gap between <Highlight>art and science</Highlight> in
                            training — supporting intuition with measurable insights.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>

            {/* Pose Estimation Section */}
            <SectionCard flexDirection="row" delay={0.1}>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Real-Time Pose Estimation – Directly in Your Browser
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            This demonstration showcases pose estimation running in real time — right inside a standard
                            browser. No special software, no setup. Just a webcam.
                        </Text>
                        <Text fontSize="md">
                            The video shows both the live camera feed and predicted pose in sync — demonstrating
                            true <Highlight>real-time capability</Highlight>.
                        </Text>
                        <Text fontSize="md">
                            We use <Highlight>DeepLabCut</Highlight> for pose prediction, but because it requires a GPU,
                            the processing happens on a <Highlight>cloud-based EC2 instance</Highlight>. Video and
                            predictions are streamed using <Highlight>WebRTC</Highlight> for ultra-low latency.
                        </Text>
                        <Text fontSize="md">
                            With this setup, we reach over <Highlight>30 frames per second</Highlight> — on par with
                            typical webcams — making it viable for live work and responsive tools.
                        </Text>
                    </Stack>
                </Box>
                <Box flex="2">
                    <AspectRatio ratio={16 / 9}>
                        <iframe
                            title="Real Time Pose Estimation in Browser"
                            src="https://www.youtube.com/embed/Ewq1nhdpfSQ"
                            allowFullScreen
                        />
                    </AspectRatio>
                </Box>
            </SectionCard>

            {/* Location Tracking Section */}
            <SectionCard flexDirection="row" delay={0.2}>
                <Box flex="2">
                    <AspectRatio ratio={16 / 9}>
                        <iframe
                            title="Location Tracking without GPS"
                            src="https://www.youtube.com/embed/3baNhoeQH9A"
                            allowFullScreen
                        />
                    </AspectRatio>
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Location Tracking – Without GPS
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            This setup shows how we track a dog’s movement in real time without using GPS — ideal for
                            use in buildings or areas where GPS is unreliable.
                        </Text>
                        <Text fontSize="md">
                            The system uses <Highlight>Ultra-Wideband (UWB)</Highlight> anchors and a wearable tag to
                            measure distances and calculate position with high precision.
                        </Text>
                        <Text fontSize="md">
                            The resulting <Highlight>x,y coordinates</Highlight> are transmitted
                            via <Highlight>Bluetooth</Highlight> to a smartphone app, which visualizes the dog’s
                            position as it moves.
                        </Text>
                        <Text fontSize="md">
                            All data is recorded automatically — enabling <Highlight>session replay</Highlight> and
                            detailed evaluation after training.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>

            {/* Emotion Detection Section */}
            <SectionCard flexDirection="row" delay={0.3}>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Exploring AI-Based Emotion Detection in Dogs
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Can AI help us interpret signs of stress or fatigue in dogs? That’s the goal of this
                            experimental project using <Highlight>multimodal AI models</Highlight> like GPT-4o.
                        </Text>
                        <Text fontSize="md">
                            The process is simple: take a photo with your phone. The AI evaluates posture, facial
                            expression, ears, and tail to estimate emotional state.
                        </Text>
                        <Text fontSize="md">
                            The goal is to provide <Highlight>additional insight</Highlight> for handlers — especially
                            when signs are subtle or difficult to observe from a distance.
                        </Text>
                        <Text fontSize="md">
                            This is an early-stage prototype and should be seen as an <Highlight>optional support
                            tool</Highlight> — not a replacement for hands-on expertise.
                        </Text>
                    </Stack>
                </Box>
                <Box flex="2">
                    <AspectRatio ratio={16 / 9}>
                        <iframe
                            title="K9Vital AI Emotion Detection"
                            src="https://www.youtube.com/embed/c_q5PnXzi54"
                            allowFullScreen
                        />
                    </AspectRatio>
                </Box>
            </SectionCard>

            {/* Sniff Detection Section */}
            <SectionCard flexDirection="row" delay={0.4}>
                <Box flex="2">
                    <AspectRatio ratio={16 / 9}>
                        <iframe
                            title="Wearable sniffing detection"
                            src="https://www.youtube.com/embed/3X1l01QYiNU"
                            allowFullScreen
                        />
                    </AspectRatio>
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Wearable Sniff Detection – Search Behavior Made Visible
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Sniffing is a key indicator of active search behavior. But from a distance, it can be hard
                            to confirm.
                        </Text>
                        <Text fontSize="md">
                            Our wearable uses <Highlight>sound classification</Highlight> to detect sniffing via a small
                            microphone and a <Highlight>neural network</Highlight> — all running directly on the device.
                        </Text>
                        <Text fontSize="md">
                            Once detected, results are sent via <Highlight>Bluetooth</Highlight> to the app — no cables,
                            no cloud, just feedback in real time.
                        </Text>
                        <Text fontSize="md">
                            This allows for <Highlight>measurable, consistent tracking</Highlight> of search behavior —
                            helping trainers fine-tune both dog and environment.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>
        </Box>
    );
};

export default SensorsPage;
